import { Clock, CreditCard, Zap, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router';

export function TrialBanner() {
  const { tenant, hasRole } = useAuth();
  const navigate = useNavigate();

  // Only show for trial tenants with known remaining days
  if (tenant?.subscription_status !== 'trial' || tenant?.trial_days_remaining == null) {
    return null;
  }

  const daysRemaining = tenant.trial_days_remaining;
  const isUrgent = daysRemaining <= 3;
  const isExpired = tenant?.trial_expired || daysRemaining <= 0;
  const isAdmin = hasRole(['admin']);

  // Format the trial end date if available
  let trialEndDate = '';
  if (tenant?.trial_ends_at) {
    try {
      trialEndDate = new Date(tenant.trial_ends_at).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (_e) {
      // Ignore date parsing errors
    }
  }

  return (
    <div 
      className={`px-4 py-2.5 flex items-center justify-between text-sm ${
        isExpired 
          ? 'bg-destructive text-destructive-foreground' 
          : isUrgent 
            ? 'bg-amber-500 text-white'
            : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white'
      }`}
      data-testid="trial-banner"
    >
      <div className="flex items-center gap-3">
        {isExpired ? (
          <>
            <Clock className="h-4 w-4 animate-pulse" />
            <span>
              Your free trial has <strong>expired</strong>. 
              {isAdmin 
                ? ' Upgrade now to continue using Eva Insights.'
                : ' Please contact your administrator to continue.'}
            </span>
          </>
        ) : isUrgent ? (
          <>
            <Clock className="h-4 w-4 animate-pulse" />
            <span>
              <strong>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</strong> left in your trial
              {trialEndDate && <span className="opacity-80 ml-1">(ends {trialEndDate})</span>}
            </span>
          </>
        ) : (
          <>
            <Zap className="h-4 w-4" />
            <span>
              <strong>Free Trial</strong> — {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
              {trialEndDate && <span className="opacity-80 ml-1">• Ends {trialEndDate}</span>}
            </span>
          </>
        )}
      </div>
      
      {/* Role-based CTA */}
      {isAdmin ? (
        <Button 
          size="sm" 
          variant="secondary"
          onClick={() => navigate('/settings/billing')}
          className={`h-7 px-3 ${
            isExpired || isUrgent
              ? 'bg-white/20 hover:bg-white/30 text-white border-0'
              : 'bg-white/20 hover:bg-white/30 text-white border-0'
          }`}
          data-testid="trial-upgrade-btn"
        >
          <CreditCard className="h-3 w-3 mr-1.5" />
          {isExpired ? 'Upgrade Now' : 'View Plans'}
        </Button>
      ) : (
        <div className="flex items-center gap-2 text-xs opacity-80">
          <Mail className="h-3 w-3" />
          <span>Contact your admin to upgrade</span>
        </div>
      )}
    </div>
  );
}
